import React, { useEffect, useState } from "react";
import { formsMenuTree, getOpenGroupIdsForPath } from "./formsMenuTree";

function FormLeaf({ item, depth, currentPath, onNavigate }) {
  const isActive = currentPath === item.path;
  return (
    <li className={`forms-tree-leaf forms-tree-depth-${depth}${isActive ? " active" : ""}`}>
      <button
        type="button"
        className="forms-tree-leaf-btn"
        onClick={() => onNavigate(item.path)}
      >
        <i className={item.icon} aria-hidden="true" />
        <span data-full-text={item.label}>{item.label}</span>
      </button>
    </li>
  );
}

function FormSubgroup({ group, parentId, depth, openGroups, onToggle, currentPath, onNavigate }) {
  const groupId = `${parentId}:${group.id}`;
  const isOpen = openGroups.has(groupId);
  const hasActiveChild = group.items?.some((item) => item.path === currentPath);

  return (
    <li className={`forms-tree-node forms-tree-depth-${depth}${hasActiveChild ? " has-active-child" : ""}`}>
      <button
        type="button"
        className={`forms-tree-toggle forms-tree-toggle--sub${isOpen ? " is-open" : ""}`}
        onClick={() => onToggle(groupId)}
        aria-expanded={isOpen}
      >
        <i className={`fa-solid fa-chevron-right forms-tree-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true" />
        {group.icon && <i className={`${group.icon} forms-tree-icon`} aria-hidden="true" />}
        <span className="forms-tree-label">{group.label}</span>
        <span className="forms-tree-count">{group.items?.length || 0}</span>
      </button>
      <ul className={`forms-tree-panel${isOpen ? " show" : ""}`}>
        {group.items?.map((item) => (
          <FormLeaf
            key={item.path}
            item={item}
            depth={depth + 1}
            currentPath={currentPath}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </li>
  );
}

function FormCategory({ category, openGroups, onToggle, currentPath, onNavigate }) {
  const isOpen = openGroups.has(category.id);
  const childPaths = [
    ...(category.items || []).map((item) => item.path),
    ...(category.children || []).flatMap((child) => child.items?.map((item) => item.path) || []),
  ];
  const hasActiveChild = childPaths.includes(currentPath);

  return (
    <li className={`forms-tree-category${hasActiveChild ? " has-active-child" : ""}`}>
      <button
        type="button"
        className={`forms-tree-toggle forms-tree-toggle--category${isOpen ? " is-open" : ""}`}
        onClick={() => onToggle(category.id)}
        aria-expanded={isOpen}
      >
        <i className={`fa-solid fa-chevron-right forms-tree-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true" />
        {category.icon && <i className={`${category.icon} forms-tree-icon`} aria-hidden="true" />}
        <span className="forms-tree-label">{category.label}</span>
      </button>
      <ul className={`forms-tree-panel${isOpen ? " show" : ""}`}>
        {category.children?.map((child) => (
          <FormSubgroup
            key={child.id}
            group={child}
            parentId={category.id}
            depth={2}
            openGroups={openGroups}
            onToggle={onToggle}
            currentPath={currentPath}
            onNavigate={onNavigate}
          />
        ))}
        {category.items?.map((item) => (
          <FormLeaf
            key={item.path}
            item={item}
            depth={2}
            currentPath={currentPath}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </li>
  );
}

export default function FormsSidebarMenu({ currentPath, onNavigate }) {
  const [openGroups, setOpenGroups] = useState(() => getOpenGroupIdsForPath(currentPath));

  useEffect(() => {
    const activeGroups = getOpenGroupIdsForPath(currentPath);
    if (activeGroups.size === 0) return;
    setOpenGroups((prev) => new Set([...prev, ...activeGroups]));
  }, [currentPath]);

  const handleToggle = (groupId) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div className="forms-sidebar-tree">
      <ul className="forms-tree-root">
        {formsMenuTree.map((category) => (
          <FormCategory
            key={category.id}
            category={category}
            openGroups={openGroups}
            onToggle={handleToggle}
            currentPath={currentPath}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>
  );
}
